import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { TROPHY_DETAILS } from "@/data/trophies";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_barcelona-club-hub/artifacts/9yydi0mf_Logotipo-La-Bombonera-%28Curvas%29.png";

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [trophiesOpen, setTrophiesOpen] = useState(false); // mobile accordion
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setTrophiesOpen(false);
  }, [location.pathname]);

  const isTitulosActive = location.pathname.startsWith("/titulos");

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#030712]/85 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="nav-logo-link"
            className="flex items-center gap-3 group"
          >
            <img
              src={LOGO_URL}
              alt="C.D. La Bombonera"
              className="h-11 w-11 object-contain transition-transform duration-500 group-hover:rotate-[6deg]"
            />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-display text-[15px] font-extrabold tracking-tight uppercase">
                La Bombonera
              </span>
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#FFB800]">
                Barcelona · 2015
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-10">
            <NavLink
              to="/"
              end
              data-testid="nav-link-inicio"
              className={({ isActive }) =>
                `text-[12px] uppercase tracking-[0.22em] font-medium link-underline transition-colors ${
                  isActive ? "text-[#FFB800]" : "text-white/80 hover:text-white"
                }`
              }
            >
              Inicio
            </NavLink>

            <NavLink
              to="/quienes-somos"
              data-testid="nav-link-quienes-somos"
              className={({ isActive }) =>
                `text-[12px] uppercase tracking-[0.22em] font-medium link-underline transition-colors ${
                  isActive ? "text-[#FFB800]" : "text-white/80 hover:text-white"
                }`
              }
            >
              Quiénes somos
            </NavLink>

            <NavLink
              to="/tienda"
              data-testid="nav-link-tienda"
              className={({ isActive }) =>
                `text-[12px] uppercase tracking-[0.22em] font-medium link-underline transition-colors ${
                  isActive ? "text-[#FFB800]" : "text-white/80 hover:text-white"
                }`
              }
            >
              Tienda
            </NavLink>

            {/* Títulos with hover dropdown */}
            <div className="relative group" data-testid="nav-titulos-dropdown">
              <Link
                to="/titulos"
                data-testid="nav-link-titulos"
                className={`flex items-center gap-1.5 text-[12px] uppercase tracking-[0.22em] font-medium transition-colors ${
                  isTitulosActive ? "text-[#FFB800]" : "text-white/80 hover:text-white"
                }`}
              >
                Títulos
                <ChevronDown
                  size={14}
                  className="transition-transform duration-300 group-hover:rotate-180"
                />
              </Link>

              {/* Dropdown panel */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                <div
                  className="bg-[#0A1121]/95 backdrop-blur-xl border border-white/10 rounded-[1.5rem] p-3 w-[420px] shadow-2xl"
                  data-testid="nav-titulos-panel"
                >
                  <Link
                    to="/#palmares"
                    data-testid="nav-titulos-home-anchor"
                    className="flex items-center justify-between gap-3 px-4 py-3 rounded-[1rem] bg-[#FFB800] text-[#030712] hover:bg-white transition-colors"
                  >
                    <span className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-[0.25em] font-mono opacity-70">
                        Atajo
                      </span>
                      <span className="font-display text-sm font-extrabold uppercase tracking-tight">
                        Ver palmarés en la home
                      </span>
                    </span>
                    <ArrowUpRight size={16} />
                  </Link>

                  <ul className="mt-2 flex flex-col">
                    {TROPHY_DETAILS.map((t, i) => (
                      <li key={t.slug}>
                        <Link
                          to={`/titulos/${t.slug}`}
                          data-testid={`nav-titulos-item-${t.slug}`}
                          className="group/item flex items-center gap-4 px-4 py-3 rounded-[1rem] hover:bg-white/5 transition-colors"
                        >
                          <span className="font-mono text-[10px] tracking-[0.25em] text-white/40 w-12">
                            {t.year}
                          </span>
                          <span className="flex-1 font-display text-sm font-bold uppercase tracking-tight text-white/90 group-hover/item:text-[#FFB800] transition-colors">
                            {t.title}
                          </span>
                          <ArrowUpRight
                            size={14}
                            className="text-white/30 group-hover/item:text-[#FFB800] group-hover/item:translate-x-0.5 transition-all"
                          />
                        </Link>
                        {i < TROPHY_DETAILS.length - 1 && (
                          <div className="h-px bg-white/5 mx-4" />
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </nav>

          {/* Right CTA */}
          <div className="hidden md:block">
            <Link
              to="/contacto"
              data-testid="nav-cta-button"
              className="btn-primary"
            >
              Contacto
            </Link>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-white p-2"
            data-testid="nav-mobile-toggle"
            aria-label="Abrir menú"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden transition-[max-height] duration-500 ${
          open ? "max-h-[640px]" : "max-h-0"
        }`}
      >
        <div className="px-6 pb-8 pt-2 flex flex-col gap-2 bg-[#030712]/95 backdrop-blur-xl border-t border-white/5">
          <NavLink
            to="/"
            end
            data-testid="nav-mobile-link-inicio"
            className={({ isActive }) =>
              `py-3 text-sm uppercase tracking-[0.22em] ${
                isActive ? "text-[#FFB800]" : "text-white/85"
              }`
            }
          >
            Inicio
          </NavLink>

          <NavLink
            to="/quienes-somos"
            data-testid="nav-mobile-link-quienes-somos"
            className={({ isActive }) =>
              `py-3 text-sm uppercase tracking-[0.22em] ${
                isActive ? "text-[#FFB800]" : "text-white/85"
              }`
            }
          >
            Quiénes somos
          </NavLink>

          <NavLink
            to="/tienda"
            data-testid="nav-mobile-link-tienda"
            className={({ isActive }) =>
              `py-3 text-sm uppercase tracking-[0.22em] ${
                isActive ? "text-[#FFB800]" : "text-white/85"
              }`
            }
          >
            Tienda
          </NavLink>

          <button
            type="button"
            onClick={() => setTrophiesOpen((v) => !v)}
            data-testid="nav-mobile-titulos-toggle"
            className="flex items-center justify-between py-3 text-sm uppercase tracking-[0.22em] text-white/85"
          >
            <span className={isTitulosActive ? "text-[#FFB800]" : ""}>Títulos</span>
            <ChevronDown
              size={16}
              className={`transition-transform duration-300 ${
                trophiesOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          <div
            className={`overflow-hidden transition-[max-height] duration-500 ${
              trophiesOpen ? "max-h-[400px]" : "max-h-0"
            }`}
          >
            <div className="flex flex-col pl-3 border-l border-white/10 ml-1 my-1 gap-1">
              <Link
                to="/#palmares"
                data-testid="nav-mobile-titulos-home"
                className="text-[11px] uppercase tracking-[0.22em] text-[#FFB800] py-2"
              >
                ↳ Ver palmarés en la home
              </Link>
              {TROPHY_DETAILS.map((t) => (
                <Link
                  key={t.slug}
                  to={`/titulos/${t.slug}`}
                  data-testid={`nav-mobile-titulos-item-${t.slug}`}
                  className="text-[12px] uppercase tracking-[0.18em] text-white/75 py-2 flex justify-between items-center gap-3"
                >
                  <span>{t.title}</span>
                  <span className="font-mono text-[10px] text-white/40">{t.year}</span>
                </Link>
              ))}
            </div>
          </div>

          <Link
            to="/contacto"
            data-testid="nav-mobile-cta"
            className="btn-primary self-start mt-4"
          >
            Contacto
          </Link>
        </div>
      </div>
    </header>
  );
}
